import { Validator, ValidationValues } from '@web-for-marketing/react-ui';
import { getValidatorMessage, hasLength, isNotEmpty, regexMatches } from '@helpers/validators';
import { regexErrorMessages, regexPatterns } from './regexHelper';
import { isSlugSegmentLengthValid } from './errors';
import { hasValue, isLengthValid } from '@helpers/operand';
import { SectionWidget } from '@models/widget';
import { SectionRiveContent } from '@models/section';
import { JurisdictionCode } from '../components/RedCarpetForm/options/jurisdictions';
import { timezoneOptions } from '../components/RedCarpetForm/options/timezones';

export const altValidators: Validator[] = [
    (value, values) =>
        (values && !values.image) || (values && values.decoration) || hasValue(value) ? null : 'Alt field is empty',
    (value, values) =>
        (values && !values.image) || (values && values.decoration) || isLengthValid({ min: 15 }, value)
            ? null
            : getValidatorMessage('Alt text should be more than 15 characters long', true),
];

export const widgetValidator: Validator = (value) => {
    function isWidget(value: unknown): value is SectionWidget {
        return typeof value === 'object' && value !== null;
    }

    if (isWidget(value)) {
        if (value.type === 'stats-highlights') {
            const errors: string[] = [];
            value.content.stats.forEach((content, index) => {
                if (content.url && !hasValue(content.ariaLabel)) {
                    errors.push(`Aria label is required for stat ${index + 1}`);
                }
            });

            return errors.length ? errors.join('.') : null;
        }
    }

    return null;
};

export const titleValidators: Validator[] = [
    isNotEmpty('A title is required'),
    hasLength(
        { max: 100 },
        'Title should not be more than 100 characters. It is recommended to be between 20-70 characters',
        true
    ),
];

export const titleHeaderSectionValidator: Validator = (value, values) =>
    !hasValue(value) && values?.headerSection ? 'A title is required for the header section' : null;

export const descriptionValidators: Validator[] = [
    isNotEmpty('A description is required'),
    hasLength({ max: 160 }, 'Description should not be more than 160 characters', true),
];

export const slugValidators: Validator[] = [
    isNotEmpty('A slug is required'),
    regexMatches(regexPatterns.slug, regexErrorMessages.slug),
    (value) =>
        typeof value === 'string' && isSlugSegmentLengthValid(value, 80)
            ? null
            : { message: 'Each slug segment should not be over 80 characters', isWarning: true },
];

export const authorNotPublishedValidator: Validator = (value, values) => {
    if (values && Array.isArray(values.authorList) && values.authorList.length > 0) {
        const author = values.authorList.find((item) => item.id === value);
        if (author && author.status === 0) {
            return {
                message:
                    'The associated author is currently unpublished. This blog post will not be accessible to the public until the author is published.',
                isWarning: true,
            };
        }
    }
    return null;
};

export const breadcrumbDisplayNameValidator: Validator = hasLength(
    { max: 30 },
    'Breadcrumb display name should not be more than 30 characters',
    true
);

export const youtubeUrlValidators: Validator[] = [
    isNotEmpty('A Youtube URL is required'),
    (value) =>
        typeof value === 'string' && (value.includes('youtube.com/') || value.includes('youtu.be/'))
            ? null
            : 'That is not a valid Youtube URL. Please try another one.',
];

export const driveShareUrlValidators: Validator[] = [
    isNotEmpty('A Google Drive public share link is required'),
    (value) =>
        typeof value === 'string' && value.includes('drive.google.com/')
            ? null
            : 'That is not a valid Google Drive share link. Please try another one.',
];

export const blogFaqValidator: Validator = (value) => {
    if (Array.isArray(value) && value.length) {
        const isNotValid = value.some((content) => content.answer === '' || content.question === '');
        return isNotValid ? 'Please fill the required fields in the FAQ accordion' : null;
    }
    return null;
};

export const riveValidator: Validator = (value) => {
    function isRive(value: unknown): value is SectionRiveContent {
        return typeof value === 'object' && value !== null && 'stateMachine' in value && 'artboard' in value;
    }

    if (isRive(value)) {
        return isNotEmpty('A Rive file is required')(value.src);
    }

    return null;
};

export const phoneValidator: Validator = (value) => {
    const phone = value as string;

    if (!phone) {
        return null;
    }

    if (!phone.startsWith('+')) {
        return 'Phone number must start with + (international format)';
    }

    if (phone.length < 8 || phone.length > 15) {
        return 'Phone number must be 8-15 characters long';
    }

    if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
        return 'Please enter a valid international phone number (e.g., +1234567890)';
    }

    return null;
};

export const timezoneValidator: Validator = (value: unknown, values?: ValidationValues) => {
    const timezone = value as string;
    const jurisdiction = values?.jurisdiction as JurisdictionCode;

    if (!timezone || !jurisdiction) {
        return null;
    }

    const timezoneOption = timezoneOptions.find((tz) => tz.value === timezone);

    if (timezoneOption && timezoneOption.jurisdiction !== jurisdiction) {
        return 'The selected Timezone does not match your jurisdiction';
    }

    return null;
};

export const databaseNameValidator: Validator = (value: unknown) => {
    const databaseName = value as string;

    if (!databaseName) return null;

    if (databaseName.length < 5) {
        return 'Database name must be at least 5 characters long';
    }

    if (!/^[a-zA-Z0-9]+$/.test(databaseName)) {
        return 'Database name can only contain letters and numbers';
    }

    return null;
};
